import { useEffect, useMemo, useState } from 'react';
import intl from 'react-intl-universal';
import { useSelector, useDispatch } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { Link } from 'react-router-dom';
import {
  Button,
  IconButton,
  Tooltip,
  Typography,
  Icon,
} from '@getsynapse/design-system';
import { filteredFormsOptions } from 'state/Forms/formSlice';
import { selectRequestsPortalName } from 'state/Organization/organizationSlice';
import {
  setSearchParameter,
  resetStatus,
  setSortParameter,
} from 'state/normalized/requests/requestsSlice';
import { SelectOrganisationId } from 'state/Organization/organizationSlice';
import { showNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import { PATHS, V2_PATHS, SORT_ORDER } from 'utils/constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FormOption } from 'utils/customTypes';
import config from 'config/Config';
import linkIcon from 'assets/icons/link.svg';
import MoreActionsPopup from 'Organisms/TableHeaderActionButtons/MoreActionsPopup';
import RequestsTable from './components/RequestsTable/RequestsTable';
import useImportRequests from './hooks/useImportRequests';
import { selectIsBusinessUser, selectIsUserLd } from 'state/User/userSlice';
import ImportRequestModal from './components/ImportRequestModal/ImportRequestModal';

const ConsolidatedRequestsListPage = () => {
  const dispatch = useDispatch();
  const importButtonProps = useImportRequests();

  const [isCopied, setIsCopied] = useState(false);
  const [showImportModal, setShowImportModal] = useState<boolean>(false);

  const formOptionsSelector: FormOption[] = useSelector(filteredFormsOptions);
  const portalName = useSelector(selectRequestsPortalName);
  const isLdUser = useSelector(selectIsUserLd);
  const isBusinessUser = useSelector(selectIsBusinessUser);
  const {
    advancedIntake = false,
    intakeAgentAccess = false,
    legacyIntake = false,
  } = useFlags();

  // The advanced intake form behaves as ON everywhere except the pure-legacy
  // customer (legacy form available, advanced form off, intake agent off). The
  // advanced form is being rolled out to everyone, so only an explicit legacy-only
  // customer should still land on the legacy create form (CSI-1785).
  const effectiveAdvanced =
    advancedIntake || intakeAgentAccess || !legacyIntake;

  const businessLoginBaseUrl = config.get('businessLoginBaseUrl');
  const organizationId = useSelector(SelectOrganisationId);
  const portalUrl = businessLoginBaseUrl + organizationId;

  const createNewRequestLink = useMemo(() => {
    if (effectiveAdvanced) {
      return V2_PATHS.SELECT_REQUEST_TYPE;
    }
    if (!isEmpty(formOptionsSelector) && formOptionsSelector[0].value) {
      return `${PATHS.REQUEST_PAGE}/${formOptionsSelector[0].value}`;
    }
    return PATHS.REQUEST_PAGE;
  }, [formOptionsSelector, effectiveAdvanced]);

  const canAccessIntakeAgent = useMemo<boolean>(() => {
    return intakeAgentAccess && (isLdUser || isBusinessUser);
  }, [intakeAgentAccess, isLdUser, isBusinessUser]);

  useEffect(() => {
    const consumeSessionFlag = (
      key: string,
      build: (requestNo: string) => {
        notificationTitle: string;
        notificationMessage?: string;
      }
    ) => {
      const requestNo = sessionStorage.getItem(key);
      if (!requestNo) return;
      sessionStorage.removeItem(key);
      dispatch(
        showNotification({
          notificationVariant: 'success',
          ...build(requestNo),
        })
      );
    };

    consumeSessionFlag('draftSavedSuccess', (requestNo) => ({
      notificationTitle: intl.get('SUCCESS_SNACKBAR_TITLE'),
      notificationMessage: intl.get(
        'REQUEST_PAGE.NOTIFICATIONS.REQUEST_CREATE_SUCCESS',
        { requestNo }
      ),
    }));

    consumeSessionFlag('requestSubmittedSuccess', () => ({
      notificationTitle: intl.get(
        'REQUEST_PAGE.NOTIFICATIONS.SUBMIT_SUCCESS_MESSAGE'
      ),
    }));

    consumeSessionFlag('requestUpdatedSuccess', (requestNo) => ({
      notificationTitle: intl.get('SUCCESS_SNACKBAR_TITLE'),
      notificationMessage: intl.get(
        'REQUEST_PAGE.NOTIFICATIONS.MODIFICATION_SUCCESS',
        { requestNo }
      ),
    }));
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setSearchParameter(''));
      dispatch(setSortParameter({ sortBy: '', sortOrder: SORT_ORDER.ASC }));
      dispatch(resetStatus());
    };
  }, [dispatch]);

  const handleCopyPortalLink = () => {
    if (!portalUrl) return;

    navigator.clipboard.writeText(portalUrl);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 5000);
  };

  return (
    <div className='flex flex-col h-full'>
      <ImportRequestModal
        showModal={showImportModal}
        onClose={() => setShowImportModal(false)}
      />
      <div className='flex justify-between items-center px-6 pt-2 pb-4 bg-neutral-white z-5'>
        <div className='flex items-center'>
          <Typography variant='h4' weight='medium'>
            {portalName}
          </Typography>
          <Tooltip
            openMode='hover1'
            position='topCenter'
            contentProps={{
              className: 'py-1 px-2 z-10 text-xs',
            }}
            showPopper
            usePortal
            timeout={0}
            trigger={
              <IconButton
                src={linkIcon}
                description={intl.get('REQUESTS_LIST_PAGE.COPY_PORTAL_LINK')}
                aria-label={intl.get('REQUESTS_LIST_PAGE.COPY_PORTAL_LINK')}
                className='ml-1'
                onClick={handleCopyPortalLink}
                iconClassname='h-5 w-5'
              />
            }
          >
            {intl.get('REQUESTS_LIST_PAGE.COPY_PORTAL_LINK')}
          </Tooltip>
          {isCopied && (
            <>
              <Icon
                name='checkmark-circle'
                className='text-success-darker ml-2 h-5 w-5 mr-1'
              />
              <Typography variant='body2'>
                {intl.get('REQUESTS_LIST_PAGE.COPY_PORTAL_LINK_SUCCESS')}
              </Typography>
            </>
          )}
        </div>
        <div className='flex items-center space-x-2'>
          {canAccessIntakeAgent && (
            <a href={V2_PATHS.AGENT_PAGE}>
              <Button variant='secondary'>
                {intl.get('REQUESTS_LIST_PAGE.ADD_REQUEST_WITH_AGENT')}
              </Button>
            </a>
          )}
          {legacyIntake && isLdUser && importButtonProps.isReady ? (
            <MoreActionsPopup
              options={[
                {
                  label: intl.get(
                    'REQUESTS_LIST_PAGE.TABLE.IMPORT.IMPORT_LEGACY'
                  ),
                  selectOption: () => setShowImportModal(true),
                  dataCy: 'request-import-button',
                  disabled:
                    isEmpty(importButtonProps.currentUser) ||
                    isEmpty(importButtonProps.settings),
                  importButtonProps: importButtonProps,
                },
              ]}
              hasSplitButton
              {...(effectiveAdvanced
                ? { externalLinkUrl: createNewRequestLink }
                : { linkUrl: createNewRequestLink })}
              buttonText={intl.get('REQUESTS_LIST_PAGE.ADD_REQUEST_BUTTON')}
              buttonTestId='add-request-button'
              // The dropdown always shows the longer "Import Legacy Request" label,
              // so the list needs the wider width regardless of effectiveAdvanced.
              listClassName='w-52'
              buttonVariant='primary'
              splitButtonVariant='primary'
            />
          ) : effectiveAdvanced ? (
            <Button
              data-testid='add-request-button'
              variant='primary'
              onClick={() => {
                window.location.href = createNewRequestLink;
              }}
            >
              {intl.get('REQUESTS_LIST_PAGE.ADD_REQUEST_BUTTON')}
            </Button>
          ) : (
            <Link to={createNewRequestLink} data-testid='add-request-button'>
              <Button variant='primary'>
                {intl.get('REQUESTS_LIST_PAGE.ADD_REQUEST_BUTTON')}
              </Button>
            </Link>
          )}
        </div>
      </div>
      <RequestsTable />
    </div>
  );
};

export default ConsolidatedRequestsListPage;
